// 1. Import the extendTheme function
import { extendTheme } from '@chakra-ui/react';

const config = {
  useSystemColorMode: false,
  initialColorMode: 'light',
};

// 2. Extend the theme to include custom colors, fonts, etc
const colors = {
  colors: {
    brightGreen: '#00D3B4',
    green: '#005F51',
    purple: '#6968af',
    grey: {
      lightest: '#f8f7f6',
      light: '#F3F0ED',
      medium: '#c1c1c1',
      dark: '#222222',
    },
  },
};

const sizes = {
  sizes: {
    container: {
      sm: '640px',
      md: '800px',
      lg: '1024px',
      xl: '1500px',
      xxl: '1920px',
    },
  },
};

const fontSizes = {
  fontSizes: {
    xs: '0.75rem',
    sm: '0.875rem',
    md: '1rem',
    lg: '1.125rem',
    xl: '1.25rem',
    '2xl': '1.5rem',
    '3xl': '1.875rem',
    '4xl': '2.25rem',
    '5xl': '3rem',
    '6xl': '3.75rem',
    '7xl': '4.5rem',
    '8xl': '6rem',
    '9xl': '8rem',
  },
};

const spacing = {
  space: {
    1: '0.0625rem',
    2: '0.125rem',
    3: '0.1875rem',
    4: '0.25rem',
    5: '0.3125rem',
    10: '0.625rem',
    20: '1.25rem',
    30: '1.875rem',
    40: '2.5rem',
    50: '3.125rem',
    60: '3.75rem',
    70: '4.375rem',
    80: '5rem',
    90: '5.625rem',
    100: '6.25rem',
  },
};

const textStyles = {
  textStyles: {
    h1: {
      fontSize: ['1.75rem', '1.75rem', '2.25rem', '3rem'],
      lineHeight: '1',
      fontWeight: '700',
      letterSpacing: ['-1px', '-1px', '-1px', '-2px'],
    },
    h2: {
      fontSize: ['1.5rem', '1.875rem', '1.875rem', '1.875rem'],
      lineHeight: '1',
      fontWeight: '700',
      letterSpacing: '-0.0625rem',
    },
    h3: {
      fontSize: ['1.25rem', '1.375rem', '1.75rem'],
      lineHeight: '1.2',
      fontWeight: '700',
    },
    h4: {
      fontSize: ['1rem', '1.125rem', '1.25rem'],
      lineHeight: '1.2',
      fontWeight: '700',
    },
    h5: {
      fontSize: ['0.875rem', '1rem', '1.125rem'],
      lineHeight: '1.2',
      fontWeight: '700',
    },
    h6: {
      fontSize: ['0.75rem', '0.875rem', '1rem'],
      lineHeight: '1.2',
      fontWeight: '700',
    },
    body: {
      fontSize: ['1rem', '1rem', '1rem'],
      lineHeight: '1.4',
      fontWeight: '400',
    },
    label: {
      fontSize: '0.875rem',
      textTransform: 'uppercase',
      fontWeight: 700,
      lineHeight: '1',
    },
  },
};

const components = {
  components: {
    Button: {
      variants: {
        primary: {
          fontWeight: '700',
          bg: 'purple',
          color: 'white',
          transition: 'all 0.25s',
          padding: '1.75rem 1.25rem',
          borderRadius: '50%',
          border: '2px solid transparent',
          _hover: {
            bg: 'white',
            color: 'purple',
            border: '2px solid',
            borderColor: 'purple',
            textDecoration: 'none',
          },
        },
        secondary: {
          fontWeight: '700',
          bg: 'transparent',
          color: 'navy',
          transition: 'all 0.25s',
          border: '2px solid navy',
          padding: '0.9375rem 0.625rem',
          borderRadius: '3.125rem',
          textTransform: 'uppercase',
          fontSize: '0.75rem',
          letterSpacing: '0.5px',
          _hover: {
            bg: 'navy',
            color: 'white',
            border: '2px solid transparent',
          },
        },
        link: {
          bg: 'none',
          color: 'navy',
          padding: '0',
          minWidth: 'auto',
          paddingInline: '0',
          _hover: {
            bg: 'none',
            border: 'none',
            textDecoration: 'underline',
          },
        },
        ghost: {
          bg: 'none',
          color: 'inherit',
          padding: '0',
          height: 'auto',
          minWidth: 'auto',
          paddingInline: '0',
          _hover: {
            bg: 'none',
            border: 'none',
            textDecoration: 'none',
          },
          _focus: {
            bg: 'none',
            border: 'none',
            textDecoration: 'none',
          },
        },
        pageNav: {
          bg: 'none',
          color: 'white',
          height: 'auto',
          padding: '0',
          whiteSpace: 'pre-wrap',
          _hover: {
            color: 'white',
            textDecoration: 'none',
          },
        },
      },
    },
    Link: {
      variants: {
        button: {
          bg: 'purple',
          color: 'white',
          transition: 'all 0.25s',
          padding: '1.25rem',
          borderRadius: '0.3125rem',
          display: 'inline-block',
          fontSize: ['1rem', '1rem', '1rem'],
          lineHeight: '1.2',
          fontWeight: '500',
          border: '2px solid transparent',
          _hover: {
            bg: 'white',
            color: 'purple',
            border: '2px solid',
            borderColor: 'purple',
            textDecoration: 'none',
          },
        },
        buttonInverse: {
          fontWeight: 'bold',
          bg: 'white',
          color: 'navy',
          transition: 'all 0.25s',
          padding: '1.5625rem 1.25rem',
          borderRadius: '3.125rem',
          display: 'inline-block',
          _hover: {
            bg: 'navy',
            color: 'white',
            textDecoration: 'none',
          },
        },
        primary: {
          bg: 'none',
          color: 'purple',
          padding: '0',
          minWidth: 'auto',
          paddingInline: '0',
          _hover: {
            bg: 'none',
            border: 'none',
            textDecoration: 'underline',
          },
        },
        ghost: {
          bg: 'none',
          color: 'inherit',
          padding: '0',
          height: 'auto',
          minWidth: 'auto',
          paddingInline: '0',
          _hover: {
            bg: 'none',
            border: 'none',
            textDecoration: 'none',
          },
          _focus: {
            bg: 'none',
            border: 'none',
            textDecoration: 'none',
          },
        },
        tab: {
          padding: 20,
          fontSize: '0.875rem',
          textTransform: 'uppercase',
          fontWeight: 500,
          letterSpacing: '0.5px',
          height: 'auto',
          lineHeight: '1',
          color: 'white',
          _hover: {
            bg: 'gray.100',
            color: 'purple',
            textDecoration: 'none',
          },
          _activeLink: {
            bg: 'white',
            color: 'purple',
            textDecoration: 'none',
          },
        },
        footer: {
          bg: 'none',
          color: 'white',
          padding: '0',
          minWidth: 'auto',
          paddingInline: '0',
          _hover: {
            bg: 'none',
            border: 'none',
            textDecoration: 'underline',
          },
        },
        pageNav: {
          bg: 'none',
          color: 'white',
          display: 'flex',
          _hover: {
            textDecoration: 'none',
          },
        },
        header: {
          bg: 'none',
          color: 'black',
          padding: '10px',
          minWidth: 'auto',
          paddingInline: '10px',
          borderRadius: '5px',
          fontSize: '1rem',
          _hover: {
            bg: 'lightBlue',
            border: 'none',
            textDecoration: 'none',
            color: 'navy',
          },
        },
      },
    },
  },
};

const theme = extendTheme({
  ...config,
  styles: {
    global: {
      body: {
        background: 'white',
        textColor: 'black',
      },
      '.framer-hidden, [data-lqip="true"]': {
        opacity: 0,
      },
      '.sanity-image': {
        transition: 'opacity 0.25s',
      },
    },
  },
  fonts: {
    body: "'Open Sans',Helvetica,Arial,Lucida,sans-serif",
    heading: "'Open Sans',Helvetica,Arial,Lucida,sans-serif",
  },
  ...colors,
  ...components,
  ...sizes,
  ...fontSizes,
  ...spacing,
  ...textStyles,
});

export const baseTheme = {};

export default theme;
